<script lang="ts">
    import { link } from 'svelte-spa-router';
    import { api } from '../../framework/js/api.js';
    import { toast } from '../../framework/js/toast.js';
    import { AppPageHeader } from '../../framework/components/app';
    import { AGENT_OPTIONS } from './knowledge/knowledgeAdmin';
    import './admin-list.css';

    let query = '';
    let agent = 'brief';
    let briefTypeUuid = '';
    let teamUuid = '';
    let loading = false;
    let preview: {
        system_context?: string;
        citations?: Array<Record<string, unknown>>;
        stats?: Record<string, unknown>;
    } | null = null;

    async function runTest(): Promise<void> {
        if (query.trim().length < 3) {
            toast.showNotification(false, 'Enter at least 3 characters to test retrieval');
            return;
        }

        loading = true;
        preview = null;
        try {
            const payload: Record<string, string> = {
                query: query.trim(),
                agent,
            };
            if (briefTypeUuid.trim()) payload.brief_type_uuid = briefTypeUuid.trim();
            if (teamUuid.trim()) payload.team_uuid = teamUuid.trim();

            const response: any = await api.testAdminKnowledgeRetrieval(payload);
            if (response?.success) {
                preview = response.data ?? null;
            } else {
                toast.showNotification(false, response?.message || 'Test failed');
            }
        } catch (error) {
            console.error(error);
            toast.showNotification(false, 'Test failed');
        } finally {
            loading = false;
        }
    }
</script>

<div class="page-shell admin-list knowledge-test-page">
    <AppPageHeader
        title="Test knowledge retrieval"
        subtitle="Preview what the AI would receive for a sample query (works even when the knowledge layer flag is off)."
        showAction={false}
    >
        <svelte:fragment slot="actions">
            <a use:link href="/admin/knowledge/platform" class="secondary-link">Platform knowledge</a>
            <a use:link href="/admin/knowledge/brief-types" class="secondary-link">Brief types</a>
        </svelte:fragment>
    </AppPageHeader>

    <div class="panel">
        <div class="field">
            <label for="test-query">Sample query</label>
            <textarea id="test-query" rows="3" bind:value={query} placeholder="e.g. How should we structure a quantitative tracker study?"></textarea>
        </div>

        <div class="field-row">
            <div class="field">
                <label for="test-agent">Agent</label>
                <select id="test-agent" bind:value={agent}>
                    {#each AGENT_OPTIONS as opt}
                        <option value={opt.value}>{opt.label}</option>
                    {/each}
                </select>
            </div>
            <div class="field">
                <label for="test-brief-type">Brief type UUID (optional)</label>
                <input id="test-brief-type" type="text" bind:value={briefTypeUuid} />
            </div>
            <div class="field">
                <label for="test-team">Team UUID (optional)</label>
                <input id="test-team" type="text" bind:value={teamUuid} />
            </div>
        </div>

        <button type="button" class="run-btn" on:click={runTest} disabled={loading}>
            {loading ? 'Running…' : 'Run preview'}
        </button>
    </div>

    {#if preview}
        <section class="results" aria-live="polite">
            {#if preview.stats}
                <p class="stats">
                    Pinned: {preview.stats.pinned_count ?? 0} · Retrieved chunks: {preview.stats.retrieved_count ?? 0}
                    {#if preview.stats.estimated_tokens}
                        · ~{preview.stats.estimated_tokens} tokens
                    {/if}
                </p>
            {/if}

            <h2>System context</h2>
            <pre class="context-block">{preview.system_context || '(empty)'}</pre>

            {#if preview.citations?.length}
                <h2>Citations</h2>
                <ul class="citations">
                    {#each preview.citations as citation}
                        <li>
                            {citation.title}
                            {#if citation.pinned}
                                <span class="tag">pinned</span>
                            {/if}
                            {#if citation.similarity != null}
                                <span class="tag">sim {Number(citation.similarity).toFixed(2)}</span>
                            {/if}
                        </li>
                    {/each}
                </ul>
            {/if}
        </section>
    {/if}
</div>

<style>
    .secondary-link {
        color: var(--primary);
        text-decoration: none;
        font-weight: 500;
        margin-right: var(--space-4);
    }

    .panel {
        background: var(--white);
        border: 1px solid var(--border-color);
        border-radius: var(--radius-card);
        padding: var(--space-5);
        box-shadow: var(--shadow-card);
        margin-bottom: var(--space-5);
    }

    .field {
        display: flex;
        flex-direction: column;
        gap: var(--space-2);
        margin-bottom: var(--space-4);
    }

    .field-row {
        display: grid;
        grid-template-columns: repeat(3, minmax(0, 1fr));
        gap: var(--space-4);
        margin-bottom: var(--space-4);
    }

    label {
        color: var(--grey);
        font-weight: 400;
    }

    input,
    select,
    textarea {
        width: 100%;
        box-sizing: border-box;
        padding: 0.75rem 1rem;
        border: 1px solid var(--border-color);
        border-radius: var(--border-radius-button);
        background: rgb(var(--input-bg));
        font: inherit;
    }

    input:focus-visible,
    select:focus-visible,
    textarea:focus-visible {
        outline: none;
        border-color: var(--primary);
        box-shadow: var(--focus-ring);
    }

    .run-btn {
        border: 0;
        background: var(--primary);
        color: var(--white);
        padding: var(--space-3) var(--space-5);
        border-radius: var(--border-radius-button);
        font-weight: 500;
        cursor: pointer;
    }

    .run-btn:disabled {
        opacity: 0.7;
        cursor: not-allowed;
    }

    .results h2 {
        font-size: var(--text-lg);
        color: var(--grey);
        margin: var(--space-4) 0 var(--space-2);
    }

    .stats {
        color: var(--grey-secondary);
        font-size: var(--text-sm);
    }

    .context-block {
        white-space: pre-wrap;
        background: var(--greyish);
        border-radius: var(--radius-card);
        padding: var(--space-4);
        font-size: var(--text-sm);
        color: var(--grey);
        max-height: 480px;
        overflow: auto;
    }

    .citations {
        margin: 0;
        padding-left: var(--space-5);
        color: var(--grey);
    }

    .tag {
        display: inline-block;
        margin-left: var(--space-2);
        padding: 0.1rem 0.4rem;
        border-radius: var(--radius-pill);
        background: var(--status-info-soft);
        color: var(--status-info-ink);
        font-size: var(--text-xs);
    }

    @media (max-width: 900px) {
        .field-row {
            grid-template-columns: 1fr;
        }
    }
</style>
